import { Texture } from 'three';
import { IDXFTextInfo } from '../types';
import { IPoint2d } from '@/editor/math/IPoint2d';

export default class TextCanvasTexture {
  private _canvas: HTMLCanvasElement;
  private _context: CanvasRenderingContext2D;
  private _texture: Texture;
  private _textList: IDXFTextInfo[] = [];
  private _highlightLayer: string | null = null;
  private _defaultTextColor = '#000000';
  private _highlightTextColor = '#53A0FD';
  // 画布包围盒，数值对应dxf中的坐标系
  private _canvasBounding = {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  };

  private _canvasPixelSize = 0;

  constructor() {
    this._canvas = document.createElement('canvas');
    const context = this._canvas.getContext('2d');
    if (!context) {
      throw new Error('Failed to create DXF text canvas context');
    }

    this._context = context;
    this._texture = new Texture(this._canvas);
  }

  setTextList(textList: IDXFTextInfo[]) {
    this._textList = textList;
    this.computeTexBoundingBox();
  }

  setHighlightLayer(layerName: string | null) {
    this._highlightLayer = layerName;
  }

  setTextColors(defaultColor: string, highlightColor: string) {
    this._defaultTextColor = defaultColor;
    this._highlightTextColor = highlightColor;
  }

  setCanvasBounding(center: IPoint2d, originSize: number, pixelSize: number) {
    this._canvasBounding = {
      left: center.x - originSize / 2,
      right: center.x + originSize / 2,
      top: center.y + originSize / 2,
      bottom: center.y - originSize / 2
    };
    this._canvasPixelSize = pixelSize;
    this._canvas.width = pixelSize;
    this._canvas.height = pixelSize;
  }

  private _getHorizontalCenterOffset(align: CanvasTextAlign, textWidth: number) {
    switch (align) {
      case 'center':
        return 0;
      case 'right':
      case 'end':
        return -textWidth / 2;
      case 'left':
      case 'start':
      default:
        return textWidth / 2;
    }
  }

  private _getVerticalCenterOffset(baseline: CanvasTextBaseline, textHeight: number) {
    switch (baseline) {
      case 'middle':
        return 0;
      case 'bottom':
      case 'alphabetic':
      case 'ideographic':
        return textHeight / 2;
      case 'top':
      case 'hanging':
      default:
        return -textHeight / 2;
    }
  }

  private _getRenderTextTopOffset(baseline: CanvasTextBaseline, lineHeight: number, size: number, lineCount: number) {
    const offset = (lineHeight - size) / 2;

    switch (baseline) {
      case 'middle':
        return offset - (lineHeight * lineCount) / 2;
      case 'bottom':
      case 'alphabetic':
      case 'ideographic':
        return offset - lineHeight * lineCount;
      case 'top':
      case 'hanging':
      default:
        return offset;
    }
  }

  // 计算每个文字项包围盒,是旋转后的包围盒
  private computeTexBoundingBox() {
    const canvasCtx = this._context;

    this._textList.forEach((textInfo) => {
      const { text, lineHeight, size, align, baseline, rotation, position } = textInfo;
      canvasCtx.font = `${size}px/${lineHeight}px Arial`;
      canvasCtx.textAlign = align;
      canvasCtx.textBaseline = baseline;

      let textHeight = 0;
      let textWidth = 0;
      text.split('\n').forEach((line) => {
        // 计算在canvas2d中的包围盒 使用canvasContext.measureText
        textWidth = Math.max(textWidth, canvasCtx.measureText(line).width);
        textHeight += lineHeight;
      });

      const rotatedWidth = textWidth * Math.abs(Math.cos(rotation)) + textHeight * Math.abs(Math.sin(rotation));
      const rotatedHeight = textWidth * Math.abs(Math.sin(rotation)) + textHeight * Math.abs(Math.cos(rotation));

      const horizontalOffset = this._getHorizontalCenterOffset(align, textWidth);
      const verticalOffset = this._getVerticalCenterOffset(baseline, textHeight);
      const centerOffsetX = horizontalOffset * Math.cos(rotation) - verticalOffset * Math.sin(rotation);
      const centerOffsetY = horizontalOffset * Math.sin(rotation) + verticalOffset * Math.cos(rotation);

      const boundingLeft = position.x + centerOffsetX - rotatedWidth / 2;
      const boundingRight = position.x + centerOffsetX + rotatedWidth / 2;
      const boundingTop = position.y + centerOffsetY + rotatedHeight / 2;
      const boundingBottom = position.y + centerOffsetY - rotatedHeight / 2;

      textInfo.rotatedBounding = {
        left: boundingLeft,
        right: boundingRight,
        top: boundingTop,
        bottom: boundingBottom
      };
    });
  }

  // 文字贴图
  renderTextToCanvas() {
    const canvasContext = this._context;
    const canvasSize = this._canvasPixelSize;
    const originHeight = this._canvasBounding.top - this._canvasBounding.bottom;
    const scale = canvasSize / originHeight;
    const minX = this._canvasBounding.left;
    const minY = this._canvasBounding.bottom;

    canvasContext.clearRect(0, 0, canvasSize, canvasSize);

    // canvasContext.strokeStyle = 'blue';
    // canvasContext.strokeRect(10, 10, canvasSize - 20, canvasSize - 20);
    // canvasContext.fillStyle = 'red';

    let textList = this._textList;
    if (textList.length > 500) {
      textList = textList.filter((textInfo) => {
        return !(
          textInfo.rotatedBounding.right < this._canvasBounding.left ||
          textInfo.rotatedBounding.left > this._canvasBounding.right ||
          textInfo.rotatedBounding.top < this._canvasBounding.bottom ||
          textInfo.rotatedBounding.bottom > this._canvasBounding.top
        );
      });
    }
    textList.forEach((textInfo) => {
      const { text, lineHeight, size, align } = textInfo;

      const x = textInfo.position.x - minX;
      const y = -(textInfo.position.y - minY) + originHeight; // canvas y轴是反的

      canvasContext.font = `${size * scale}px/${lineHeight * scale}px Arial`;
      canvasContext.textAlign = align;
      // canvasContext.textBaseline = baseline;
      canvasContext.textBaseline = 'top';
      canvasContext.fillStyle =
        this._highlightLayer !== null && textInfo.layer === this._highlightLayer
          ? this._highlightTextColor
          : this._defaultTextColor;

      // 调试框
      // const { left, right, top, bottom } = textInfo.rotatedBounding;
      // const boxLeft = left - minX;
      // const boxRight = right - minX;
      // const boxBottom = -(top - minY) + originHeight;
      // const boxTop = -(bottom - minY) + originHeight;
      // canvasContext.fillStyle = 'purple';
      // canvasContext.strokeRect(
      //   boxLeft * scale,
      //   boxTop * scale,
      //   (boxRight - boxLeft) * scale,
      //   (boxBottom - boxTop) * scale
      // );
      // canvasContext.fillStyle = 'red';
      // canvasContext.strokeRect(x * scale, y * scale, 10, 10);

      canvasContext.save();
      // 进行坐标系变换，用于旋转字体
      canvasContext.translate(x * scale, y * scale);
      if (textInfo.rotation) canvasContext.rotate(-textInfo.rotation);
      const lines = text.split('\n');
      let offset = this._getRenderTextTopOffset(textInfo.baseline, lineHeight, size, lines.length);

      lines.forEach((lineText) => {
        canvasContext.fillText(lineText, 0, offset * scale);
        offset += lineHeight;
      });
      canvasContext.restore();
    });

    this._texture.needsUpdate = true;
  }

  getTexture() {
    return this._texture;
  }

  dispose() {
    this._texture.dispose();
  }
}
